import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { compose } from 'redux';
import injectSheet from 'react-jss';
import Input from '../Form/Input';
import RadioGroup from '../Form/RadioGroup';
import RadioButton from '../Form/RadioButton';
import { getSliceContent } from '../../../selectors';
import TCCheckbox from '../Form/TCCheckbox';
import Button from '../Form/Button';
import { colourThemeType } from '../../../propTypes';
import theme from '../../../styles/theme';
import getConfig from './config';
import InterestsCheckbox from '../Form/InterestsCheckbox';

class CompetitionForm extends React.PureComponent {
  constructor(props) {
    super(props);
    const config = getConfig(props);
    this.state = {
      config,
      form: {
        initialState: {},
        validation: {},
      },
    };
    this.errors = {};
    this.touched = {};
  }

  componentDidMount() {
    this.setState({ form: this.buildForm() });
  }

  handleSubmit = ({
    story,
    tc,
    interests,
    newsletter,
    thirdParty,
    age,
    phonenumber,
    postcode,
    ...values
  }, { setSubmitting }) => {
    const { onSubmit, promotionId } = this.props;

    setSubmitting(true);
    const payload = {
      ...values,
      promotion: promotionId,
      source: 'promotion',
    };
    const customFields = {
      story,
      interests,
      phonenumber,
      postcode,
      optIn: {
        tc,
        newsletter,
        thirdParty,
        age,
      },
    };

    onSubmit({ ...payload, customFields });
  };

  // eslint-disable-next-line react/destructuring-assignment
  shouldRender = key => key.startsWith('show') && this.props[key];

  /**
   * This builds out the initial form state and validation rules
   * based on what is selected in Prismic.
   */
  buildForm() {
    const propsKeys = Object.keys(this.props);
    const { config } = this.state;

    // eslint-disable-next-line react/destructuring-assignment
    const toRender = propsKeys.filter(key => key.startsWith('show') && this.props[key] === true);

    const formInitialState = {};
    const validationSchema = {};

    toRender.forEach(key => {
      formInitialState[config[key].id] = config[key].initialState;
      validationSchema[config[key].id] = config[key].validation;
    });

    return {
      initialState: {
        tc: config.tc.initialState,
        newsletter: config.newsletter.initialState,
        thirdParty: config.thirdParty.initialState,
        age: config.age.initialState,
        ...formInitialState,
      },
      validation: Yup.object().shape({
        tc: config.tc.validation,
        newsletter: config.newsletter.validation,
        age: config.age.validation,
        ...validationSchema,
      }),
    };
  }

  renderGenderOptions = () => {
    const { config } = this.state;
    return [
      <RadioButton key="female" id="female" name={config.showGender.id} value="female" label="Womenswear" defaultChecked />,
      <RadioButton key="male" id="male" name={config.showGender.id} value="male" label="Menswear" />,
    ];
  };

  renderCountryOptions = () => {
    const { config } = this.state;
    return [
      <RadioButton key="aus" id="aus" name={config.showCountries.id} value="AU" label="Australia" defaultChecked />,
      <RadioButton key="nz" id="nz" name={config.showCountries.id} value="NZ" label="New Zealand" />,
    ];
  };

  renderTextarea = field => <textarea {...field} rows={4} />;

  renderInput = (key, type, label, required, render) => {
    if (!this.shouldRender(key)) return null;

    const { config } = this.state;
    const props = {
      required,
      type,
      label,
      id: config[key].id,
      errors: this.errors,
      touched: this.touched,
    };
    if (render) {
      props.render = ({ field }) => render(field);
    }
    return <Input {...props} />;
  };

  renderRadioGroup = (key, title, required, options) => {
    if (!this.shouldRender(key)) return null;

    const { config } = this.state;
    const props = {
      title,
      required,
      buttons: options(),
      name: config[key].id,
    };
    return <RadioGroup {...props} />;
  };

  render() {
    const {
      classes,
      submitButtonText,
      textAreaTitle,
      showTextArea,
      showInterests,
      sliceId,
      colourTheme,
    } = this.props;
    const { form: { initialState, validation } } = this.state;
    return (
      <Formik
        validateOnBlur
        enableReinitialize
        initialValues={initialState}
        validationSchema={validation}
        onSubmit={this.handleSubmit}
      >
        {({
          isSubmitting, errors, touched,
        }) => {
          this.errors = errors;
          this.touched = touched;
          return (
            <Form>
              <div className={classes.row}>
                {showTextArea && (
                  <div className={classes.group}>
                    {this.renderInput('showTextArea', 'textarea', textAreaTitle, true, this.renderTextarea)}
                  </div>
                )}
                {this.renderInput('showFirstName', 'text', 'First name')}
                {this.renderInput('showLastName', 'text', 'Last name')}
                {this.renderInput('showEmail', 'email', 'Email address')}
                {this.renderInput('showPostcode', 'text', 'Postcode')}
                {this.renderInput('showPhoneNumber', 'text', 'Phone number')}
                {this.renderInput('showDob', 'date', 'Date of birth')}
                {showInterests && (
                  <div className={classes.group}>
                    {this.renderRadioGroup('showInterests', 'What are you interested in?', false, () => <InterestsCheckbox />)}
                  </div>
                )}
                {this.renderRadioGroup('showGender', 'Shopping preference', true, this.renderGenderOptions)}
                {this.renderRadioGroup('showCountries', 'Country', true, this.renderCountryOptions)}
              </div>

              <TCCheckbox sliceId={sliceId} colourTheme={colourTheme} />
              <Button
                type="submit"
                disabled={isSubmitting}
                text={submitButtonText}
                colourTheme={colourTheme}
              />
            </Form>
          );
        }}
      </Formik>
    );
  }
}

export const defaultSubmitButtonText = 'Submit';

CompetitionForm.defaultProps = {
  textAreaTitle: '',
  submitButtonText: defaultSubmitButtonText,
};

CompetitionForm.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  showDob: PropTypes.bool.isRequired,
  showCountries: PropTypes.bool.isRequired,
  showEmail: PropTypes.bool.isRequired,
  showFirstName: PropTypes.bool.isRequired,
  showGender: PropTypes.bool.isRequired,
  showInterests: PropTypes.bool.isRequired,
  showLastName: PropTypes.bool.isRequired,
  showPhoneNumber: PropTypes.bool.isRequired,
  showPostcode: PropTypes.bool.isRequired,
  showTextArea: PropTypes.bool.isRequired,
  sliceId: PropTypes.number.isRequired,
  submitButtonText: PropTypes.string,
  textAreaTitle: PropTypes.string,
  textAreaLimitWords: PropTypes.bool.isRequired,
  colourTheme: colourThemeType.isRequired,
  onSubmit: PropTypes.func.isRequired,
  promotionId: PropTypes.string.isRequired,
};

const showField = (state, sliceId, field) => getSliceContent(state, sliceId)[field] === 'true';

const mapStateToProps = (state, { sliceId }) => ({
  showDob: showField(state, sliceId, 'show_dob'),
  showCountries: showField(state, sliceId, 'show_countries'),
  showEmail: showField(state, sliceId, 'show_email_address'),
  showFirstName: showField(state, sliceId, 'show_first_name'),
  showGender: showField(state, sliceId, 'show_gender'),
  showInterests: showField(state, sliceId, 'show_interests_dropdown'),
  showLastName: showField(state, sliceId, 'show_last_name'),
  showPhoneNumber: showField(state, sliceId, 'show_phone_number'),
  showPostcode: showField(state, sliceId, 'show_post_code'),
  showTextArea: showField(state, sliceId, 'show_text_area'),
  submitButtonText: getSliceContent(state, sliceId).submit_button_text || undefined,
  textAreaTitle: getSliceContent(state, sliceId).text_area_title,
  textAreaLimitWords: showField(state, sliceId, 'text_area_limit_words'),
  promotionId: getSliceContent(state, sliceId).promotion_id,
});

export default compose(
  connect(mapStateToProps),
  injectSheet({
    row: {
      width: '100%',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      flexDirection: 'row',
      '& > div': {
        flex: '1 1 50%',
        minWidth: '100%',
        [theme.breakpoints.up('md')]: {
          minWidth: '50%',
        },
        [theme.breakpoints.up('lg')]: {
          minWidth: '100%',
        },
        [theme.breakpoints.up('xl')]: {
          minWidth: '50%',
        },
      },
    },
    group: {
      minWidth: '100% !important',
      display: 'flex',
    },
  }),
)(CompetitionForm);
